import { getCurrentUser } from '../services/authService';
import { USER_TYPES } from './constants';
import { USERS_WITH_VIEW_ALL_REPORTS_PERMISSION } from './ids';
import { Project, User } from './models';

import config from './config';

const getIsReportViewer =  (project: Project) => {
	const user = getCurrentUser() as User;
	return user.userType === USER_TYPES.EXTERNAL && user.role.viewAssociatedReports && user.organisationId === project.organisationId;
};

const getIsContactPerson = (project: Project) => {
	const user = getCurrentUser() as User;
	return user.userType === USER_TYPES.EXTERNAL && user.role.viewReports && user.organisationId === project.organisationId;
};

const getClientCanAccessReport = (project: Project) => {
	return (config.enableEarlyClientAccess) ? project.report?.isReviewed : project.report?.approved;
};

const getIsOrganizationProject = (project: Project) => {
	const user = getCurrentUser() as User;
	const isSameOrganization = project.organisationId === user.organisationId;

	return isSameOrganization && getClientCanAccessReport(project);
};

const getInternalReportAccess = (project: Project) => {
	const user = getCurrentUser() as User;
	
	// must be in the same division
	if (!user.division.includes(project.divisionId)) {
		return false;
	}

	// approvers and reviewers can access all reports, supervisors can access theirs.
	if (project.supervisorId === user.id || user.role.approveReports || user.role.reviewReports) {
		return true;
	}

	return false;
};

const getCanAccessReportInternally = (project: Project) => {
	const user = getCurrentUser() as User;
	
	if (user.userType === USER_TYPES.EXTERNAL) {
		return false;
	}

	return getInternalReportAccess(project);
};

const getCanAccessReportDetails = (project: Project) => {
	const user = getCurrentUser() as User;

	if (user.userType === USER_TYPES.EXTERNAL) {
		const isAllowedToAccessReport = getIsReportViewer(project) || getIsContactPerson(project);
		return getClientCanAccessReport(project) && isAllowedToAccessReport;
	}
	
	if (USERS_WITH_VIEW_ALL_REPORTS_PERMISSION.includes(user.id.toString())) {
		return true;
	}

	return getInternalReportAccess(project);
};

const getCanAccessReportProgress = (project: Project) => {
	const user = getCurrentUser() as User;
	
	if (user.userType === USER_TYPES.EXTERNAL) {
		return getClientCanAccessReport(project) && getIsContactPerson(project);
	}
	
	return getInternalReportAccess(project);
};

const getCanAccessReportCreation = (project: Project) => {
	return getCanAccessReportInternally(project) && !project.report.approved;
};


export { getIsContactPerson, getIsReportViewer, getClientCanAccessReport, getIsOrganizationProject, getCanAccessReportInternally, getCanAccessReportDetails, getCanAccessReportProgress, getCanAccessReportCreation };
